import { FolderTreeItem, useFolderActionPermissions } from "@zapier/client";
import {
  KebabMenuItem,
  useEmitFolderActionEvent,
  FolderActionInteractionOption,
  FolderModal,
} from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";
import { useMemo } from "react";
import { FolderTypeQueryState, useNotification } from "@zapier/utils";

interface Input {
  folder?: FolderTreeItem;
  folderLevel: number;
  setCurrentModal: (modal: FolderModal | undefined) => void;
  setCurrentFolder: (folder: FolderTreeItem | undefined) => void;
}

export function useFolderActionItems({
  folder,
  folderLevel,
  setCurrentFolder,
  setCurrentModal,
}: Input) {
  const { createNotification } = useNotification();
  const { emit } = useEmitFolderActionEvent();
  const tAction = useTranslations("buttons");

  const {
    canMove,
    canRename,
    disabledDeleteMessage,
    disabledMoveMessage,
    disabledShareMessage,
    canCreateSubfolder,
    canCreateSubfolderLimit,
    subfolderLimitMessage,
    isLoading,
  } = useFolderActionPermissions({
    folderId: folder?.id,
    folderDepth: folderLevel + 1, // Pass child's depth to check if creation is allowed
  });

  return useMemo(() => {
    const items: KebabMenuItem[] = [
      {
        icon: "actionEdit",
        label: "Rename",
        onClick: () => {
          setCurrentFolder(folder);
          setCurrentModal(FolderModal.Rename);
        },
        disabled: isLoading || !canRename,
      },
      {
        icon: "personAdd",
        label: "Share folder",
        onClick: () => {
          setCurrentFolder(folder);
          setCurrentModal(FolderModal.Share);
        },
        disabled: isLoading || !!disabledShareMessage,
        tooltipText: disabledShareMessage,
      },
      { divider: true, label: "divider" },
      {
        icon: "navFolder",
        label: "Create subfolder",
        onClick: () => {
          // Cannot create a subfolder more than 5 levels deep
          if (canCreateSubfolderLimit) {
            emit({
              optionSelected: FolderActionInteractionOption.CreateSubfolder,
              interactionRawText: folder?.isShared
                ? FolderTypeQueryState.Shared
                : FolderTypeQueryState.Personal,
            });
            setCurrentFolder(folder);
            setCurrentModal(FolderModal.Create);
          } else {
            createNotification({
              message: subfolderLimitMessage,
              type: "warning",
            });
          }
        },
        disabled: isLoading || !canCreateSubfolder,
      },
      {
        icon: "actionMoveToFolder",
        label: tAction("move-to-folder"),
        onClick: () => {
          emit({
            optionSelected: FolderActionInteractionOption.MoveFolder,
          });
          setCurrentFolder(folder);
          setCurrentModal(FolderModal.Move);
        },
        disabled: isLoading || !canMove,
        tooltipText: disabledMoveMessage || undefined,
      },
      { divider: true, label: "divider" },
      {
        icon: "actionTrash",
        label: tAction("delete"),
        onClick: () => {
          setCurrentFolder(folder);
          setCurrentModal(FolderModal.Delete);
        },
        disabled: isLoading || Boolean(disabledDeleteMessage),
        hasWarning: true,
        tooltipText: disabledDeleteMessage,
      },
    ];

    return items;
  }, [
    isLoading,
    canRename,
    disabledShareMessage,
    canCreateSubfolder,
    canCreateSubfolderLimit,
    canMove,
    disabledMoveMessage,
    disabledDeleteMessage,
    folder,
    setCurrentFolder,
    setCurrentModal,
    emit,
    createNotification,
    subfolderLimitMessage,
    tAction,
  ]);
}
